import { BadgePill, brandfolderColorPalette, PillSize } from '@brandfolder/react';
import { t, Trans } from '@lingui/macro';
import React, { useEffect, useState, FunctionComponent, ReactNode } from 'react';

import { update as updateResource } from '@api/v4/resources/resources';

// eslint-disable-next-line max-len
import PeopleTaggingDisableModal from '@components/settings/brandfolder/advanced-tab/people-tagging/PeopleTaggingDisableModal';

import classes from './styles/settings-enable-disable.module.scss';

interface SettingsEnableDisableProps {
  defaultEnabled: boolean | null;
  errorTitle: string;
  id: string;
  successTitle: string;
  title: string;
  updateResourceKey: string;
  description?: ReactNode;
  onUpdate?: (enabled: boolean) => void;
  showDisableInfoModal?: boolean;
  isNew?: boolean;
  statusBadge?: ReactNode;
  successBody?: string;
  successTitleDisabled?: string;
}

export const SettingsEnableDisable: FunctionComponent<SettingsEnableDisableProps> = ({
  defaultEnabled,
  description = undefined,
  errorTitle,
  id,
  onUpdate = undefined,
  successTitle,
  title,
  updateResourceKey,
  showDisableInfoModal = false,
  isNew = false,
  statusBadge = undefined,
  successBody = undefined,
  successTitleDisabled = undefined,
}) => {

  const [enabled, setEnabled] = useState(defaultEnabled || false);
  const [mounted, setMounted] = useState(false);
  const [showInfoModal, setShowInfoModal] = useState(false);

  const handleSubmit = async (newEnabledValue: boolean): Promise<void> => {
    try {
      await updateResource({
        key: BFG?.resource?.key,
        resourceType: BFG?.resource?.type,
        [updateResourceKey]: newEnabledValue
      });

      const notificationTitle = !newEnabledValue && successTitleDisabled
        ? successTitleDisabled
        : successTitle.replace('<status>', newEnabledValue ? t`enabled` : t`disabled`);

      Notify.create({
        title: notificationTitle,
        type: 'success',
        ...(newEnabledValue && successBody ? { body: successBody } : {})
      });

      if (onUpdate) {
        onUpdate(newEnabledValue);
      }
    } catch {
      Notify.create({
        title: errorTitle,
        type: 'error'
      });
    }
  };

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    if (mounted) {
      handleSubmit(enabled);
    }
  }, [enabled]); // eslint-disable-line react-hooks/exhaustive-deps

  // Handler for clicking the "Disabled" radio button
  const handleDisableClick = (): void => {
    if (showDisableInfoModal) {
      setShowInfoModal(true);
    } else {
      setEnabled(false);
    }
  };

  // Modal cancel - just close modal, state remains unchanged
  const handleModalCancel = (): void => {
    setShowInfoModal(false);
  };

  // Modal confirm - close modal and update state (triggers submit via useEffect)
  const handleModalConfirm = (): void => {
    setShowInfoModal(false);
    setEnabled(false);
  };

  return (
    <div className={classes.container}>
      <div className="advanced-tab-settings__row">
        <div className="quarter-column">
          <p className="advanced-setting-title">
            {title}
            {isNew && <BadgePill
              backgroundColor={brandfolderColorPalette.primary[100]}
              className='new-badge-pill'
              fontColor={brandfolderColorPalette.primary[700]}
              fontSize={12}
              radius={5}
              size={PillSize.XSmall}
              >
                <Trans>New</Trans>
              </BadgePill>
          }
            {statusBadge}
          </p>
          {description && <p className="advanced-setting-description">{description}</p>}
        </div>
        <div className="three-quarters-column">
          <div className="advanced-tab-settings__options">
            <div className="radio-button-container">
              <input
                checked={enabled}
                // adding no-trigger will prevent the aggressive unsaved
                // changes function alertOfUnsavedChanges from running on these autosaves
                className="radio-margin no-trigger"
                id={`${id}-setting-enabled`}
                name={`${id}-setting-enabled-disabled`}
                onChange={(): void => setEnabled(true)}
                type="radio"
                value="true"
              />
              <label htmlFor={`${id}-setting-enabled`}>
                <Trans>Enabled</Trans>
              </label>
            </div>
            <div className="radio-button-container">
              <input
                checked={!enabled}
                // adding no-trigger will prevent the aggressive unsaved
                // changes function alertOfUnsavedChanges from running on these autosaves
                className="radio-margin no-trigger"
                id={`${id}-setting-disabled`}
                name={`${id}-setting-enabled-disabled`}
                onChange={handleDisableClick}
                type="radio"
                value="false"
              />
              <label htmlFor={`${id}-setting-disabled`}>
                <Trans>Disabled</Trans>
              </label>
            </div>
          </div>
        </div>
      </div>
      <PeopleTaggingDisableModal
        isOpen={showInfoModal}
        onCancel={handleModalCancel}
        onDisable={handleModalConfirm}
      />
    </div>
  );
};
